import { tUndeliveredMessage } from './components/TemplatesTable/UndeliveredMessage.types.ts';
import { tTemplateTable } from './components/TemplatesTable/TemplatesTable.types.ts';
import { tSentTemplateCounter } from './components/TemplatesTable/SentTemplateCounter.types.ts';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  eBusinessVerificationBucket,
  eBusinessVerificationStatus,
  eCanSendMessage,
  eComponentType,
  eFormatType,
  eParameterFormat,
  eQualityRating,
  eTemplateCategory,
  eWhatsappApiWindow,
} from './WhatsappApi.enum.ts';
import {
  tAccountInfo,
  tButton,
  tComponent,
  tCreateTemplateRequest,
  tAccountHealthStatus,
  tCreditsBalance,
  tCreditPackage,
  tCreditsInfo,
  tGetAccountResponse,
  tGetStatisticsResponse,
  tMessageStatistics,
  tNotificationCategory,
  tPaginated,
  tPaymentMethod,
  tPendingFlowForTemplate,
  tPurchaseDraft,
  tPurchaseHistoryItem,
  tPurchaseStep,
  tStatisticsDateRange,
  tUsageRecord,
  tWapiNotification,
  tWhatsappApiHook,
  eMediaType,
} from './WhatsappApi.types.ts';
import { useFormik } from 'formik';
import { initialCreateTemplateFormikValues } from './WhatsappApi.constants.ts';
import { eSelectedInput } from './components/CreateTemplate/CreateOrEditTemplate.enum.ts';
import { WhatsappApiSchema } from 'src/pages/WhatsappApi/WhatsappApi.schema.ts';
import { WhatsappApiService } from 'src/pages/WhatsappApi/WhatsappApi.service.ts';
import { CreditsService } from 'src/pages/WhatsappApi/Credits.service.ts';
import { useWorkspaceContext } from 'src/providers/WorkspaceProvider/Workspace.context.ts';
import { useBillingPermissions } from 'src/providers/ACLProvider/useBillingPermissions.hook';
import { useToastContext } from 'src/providers/ToastProvider/Toast.hook.ts';
import { eAlertType } from 'src/providers/ToastProvider/Toast.enum.ts';
import { tVariable } from './components/CreateTemplate/components/DataTemplate/DataTemplate.types.ts';
import { useErrorHandler } from 'src/hooks/useErrorHandler.hook';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { eTemplatesTable } from 'src/pages/WhatsappApi/components/TemplatesTable/TemplatesTable.enum.ts';
import { eIntegrationStatus } from 'src/providers/IntegrationsProvider/Integrations.enums.ts';
import { useIntegrationsContext } from 'src/providers/IntegrationsProvider/Integrations.context.ts';
import useTemplatesTable from 'src/pages/WhatsappApi/components/TemplatesTable/TemplatesTable.hook.ts';
import {
  applyTemplateStatusUpdate,
  toRejectionInfo,
} from 'src/pages/WhatsappApi/components/TemplatesTable/TemplatesTable.functions';
import { useSocketsContext } from 'src/providers/SocketsProvider/Sockets.context.ts';
import { useWalletFlag } from 'src/pages/Wallet/hooks/useWalletFlag';
import { useTemplateDraftsState } from 'src/pages/WhatsappApi/hooks/useTemplateDraftsState';
import { hasWapiAccountRecord } from 'src/pages/WhatsappApi/hooks/useWapiNotConnected';
import {
  tAccountHealthUpdatedEvent,
  tCreateNewWapiTemplateEvent,
  tUpdateWapiTemplateEvent,
  tWapiNotificationCreatedEvent,
} from 'src/providers/SocketsProvider/Sockets.types.ts';
import dayjs from 'dayjs';
import { useWapiAnalytics } from 'src/hooks/analytics/useWapiAnalytics.hook';
import { WAPI_EVENTS } from 'src/hooks/analytics/wapiAnalytics.constants';
import { stripePromise } from 'src/constants/stripe.constants.ts';
import { mapCreditsServiceError } from 'src/pages/WhatsappApi/Credits.service.ts';
import { sortPaymentMethodsByDefaultFirst } from 'src/pages/WhatsappApi/Credits.functions.ts';
import { OneTimeCreditsService } from 'src/pages/WhatsappApi/components/modals/PurchaseCreditsModal/OneTimeCredits.service.ts';
import {
  tOneTimeCreditProduct,
  tPaymentMethodOption,
} from 'src/pages/WhatsappApi/components/modals/PurchaseCreditsModal/PurchaseCreditsModal.types.ts';

const MESSAGING_LIMIT_TIERS: Record<string, number> = {
  TIER_250: 250,
  TIER_1K: 1000,
  TIER_2K: 2000,
  TIER_10K: 10000,
  TIER_100K: 100000,
  TIER_UNLIMITED: -1,
};

const QUALITY_RATING_MAP: Record<string, eQualityRating> = {
  HIGH: eQualityRating.HIGH,
  MEDIUM: eQualityRating.MEDIUM,
  LOW: eQualityRating.LOW,
};

const QUALITY_LABEL_KEYS: Record<string, string> = {
  HIGH: 'page.general.quality_labels.high',
  MEDIUM: 'page.general.quality_labels.medium',
  LOW: 'page.general.quality_labels.low',
  UNKNOWN: 'page.general.quality_labels.unknown',
};

/**
 * Collapses Meta's 11 verification states into the 3 visual buckets the
 * AccountInfoCard renders (verde / amarillo / rojo). UNKNOWN — including
 * NOT_VERIFIED, REJECTED, and any value the backend has catalogued as REJECTED-like —
 * is the safe degraded state when the backend cannot reach Meta.
 */
const BUSINESS_VERIFICATION_BUCKET_MAP: Record<string, eBusinessVerificationBucket> = {
  [eBusinessVerificationStatus.VERIFIED]: eBusinessVerificationBucket.VERIFIED,
  [eBusinessVerificationStatus.PENDING]: eBusinessVerificationBucket.PENDING,
  [eBusinessVerificationStatus.PENDING_NEED_MORE_INFO]: eBusinessVerificationBucket.PENDING,
  [eBusinessVerificationStatus.PENDING_SUBMISSION]: eBusinessVerificationBucket.PENDING,
  [eBusinessVerificationStatus.NOT_VERIFIED]: eBusinessVerificationBucket.REJECTED,
  [eBusinessVerificationStatus.EXPIRED]: eBusinessVerificationBucket.REJECTED,
  [eBusinessVerificationStatus.REJECTED]: eBusinessVerificationBucket.REJECTED,
  [eBusinessVerificationStatus.REVOKED]: eBusinessVerificationBucket.REJECTED,
  [eBusinessVerificationStatus.FAILED]: eBusinessVerificationBucket.REJECTED,
  [eBusinessVerificationStatus.INELIGIBLE]: eBusinessVerificationBucket.REJECTED,
  [eBusinessVerificationStatus.UNKNOWN]: eBusinessVerificationBucket.UNKNOWN,
};

const BUSINESS_VERIFICATION_LABEL_KEYS: Record<string, string> = {
  [eBusinessVerificationStatus.VERIFIED]:
    'page.general.account_info_card.business_verification.verified',
  [eBusinessVerificationStatus.NOT_VERIFIED]:
    'page.general.account_info_card.business_verification.not_verified',
  [eBusinessVerificationStatus.PENDING]:
    'page.general.account_info_card.business_verification.pending',
  [eBusinessVerificationStatus.PENDING_NEED_MORE_INFO]:
    'page.general.account_info_card.business_verification.pending_need_more_info',
  [eBusinessVerificationStatus.PENDING_SUBMISSION]:
    'page.general.account_info_card.business_verification.pending_submission',
  [eBusinessVerificationStatus.EXPIRED]:
    'page.general.account_info_card.business_verification.expired',
  [eBusinessVerificationStatus.REJECTED]:
    'page.general.account_info_card.business_verification.rejected',
  [eBusinessVerificationStatus.REVOKED]:
    'page.general.account_info_card.business_verification.revoked',
  [eBusinessVerificationStatus.FAILED]:
    'page.general.account_info_card.business_verification.failed',
  [eBusinessVerificationStatus.INELIGIBLE]:
    'page.general.account_info_card.business_verification.ineligible',
  [eBusinessVerificationStatus.UNKNOWN]:
    'page.general.account_info_card.business_verification.unknown',
};

/**
 * Validates and normalizes the backend `business_verification_status` string
 * into the catalogued enum. Returns null when the field is absent so the card
 * can hide the row (vs. rendering a misleading badge for missing data).
 *
 * Why runtime validation: the backend collapses unknown values to UNKNOWN, but
 * a stale frontend bundle reading a brand-new value Meta added would otherwise
 * render undefined labels. Cross-checking against eBusinessVerificationStatus
 * keeps us forward-compatible — see
 * docs/solutions/logic-errors/truthy-invalid-enum-bypasses-fallback.md.
 */
export function resolveBusinessVerificationStatus(
  raw: string | undefined | null,
): eBusinessVerificationStatus | null {
  if (raw == null) return null;
  const normalized = raw.toUpperCase();
  const allowed = Object.values(
    eBusinessVerificationStatus,
  ) as readonly string[];
  if (!allowed.includes(normalized)) {
    return eBusinessVerificationStatus.UNKNOWN;
  }
  return normalized as eBusinessVerificationStatus;
}

/**
 * Validates and normalizes the backend `can_send_message` string into the
 * catalogued enum. Returns null when the field is absent OR carries a value the
 * frontend does not recognize — eCanSendMessage has no UNKNOWN member, so the
 * connection badge degrades to its binary connected/disconnected fallback in
 * both cases.
 *
 * Runtime validation (vs. an `as`-cast of raw input) keeps a stale frontend
 * bundle forward-compatible with any new value Meta adds — see
 * docs/solutions/logic-errors/truthy-invalid-enum-bypasses-fallback.md.
 */
export function resolveCanSendMessage(
  raw: string | undefined | null,
): eCanSendMessage | null {
  if (raw == null) return null;
  const normalized = raw.toUpperCase();
  const allowed = Object.values(eCanSendMessage) as readonly string[];
  if (!allowed.includes(normalized)) {
    return null;
  }
  return normalized as eCanSendMessage;
}

/**
 * @deprecated Kept as the rolling-deploy fallback when the backend has not yet
 * shipped `daily_messaging_limit` (INT-901). Prefer `resolveDailyMessageLimit`,
 * which reads the resolved number from the backend and only falls back to this
 * map when needed. This map and function will be removed in a follow-up release
 * once the backend has been deployed long enough for stale clients to clear.
 */
export function mapMessagingLimitTier(tier?: string): number {
  if (tier && MESSAGING_LIMIT_TIERS[tier] !== undefined) {
    return MESSAGING_LIMIT_TIERS[tier];
  }
  return 0;
}

/**
 * Resolves the daily messaging limit a campaign-creation guard or a quota
 * progress bar should use. The backend (whatsapp-business-service) returns the
 * already-resolved number in `daily_messaging_limit` since INT-901; we prefer
 * that. Older backend versions only return `messaging_limit_tier`, so we fall
 * back to the legacy in-process tier→number map to keep the rolling deploy
 * window safe. Returns `0` when neither is available, which is the
 * existing "no daily allowance" UX.
 */
export function resolveDailyMessageLimit(account: {
  daily_messaging_limit?: number;
  messaging_limit_tier?: string;
}): number {
  if (typeof account.daily_messaging_limit === 'number') {
    return account.daily_messaging_limit;
  }
  return mapMessagingLimitTier(account.messaging_limit_tier);
}

// usage_records arrives sorted ASC by period_end from the backend.
// The plan row has the longest period, so it lands last in the array.
export function deriveCreditsDateInfo(
  usageRecords: tUsageRecord[],
  planIncludedLimit: number,
): { planPeriodEnd: string; addonExpirationDate: string } {
  const activeRecords = usageRecords.filter((r) => r.is_active);
  const planRecord =
    activeRecords.find((r) => r.limit === planIncludedLimit) ??
    activeRecords[activeRecords.length - 1]; // fallback: plan row is last (ASC sort)
  const nextExpiringAddon = activeRecords.filter(
    (r) => r.limit !== planIncludedLimit,
  )[0];
  return {
    planPeriodEnd: planRecord?.period_end ?? '',
    addonExpirationDate:
      nextExpiringAddon?.period_end ?? planRecord?.period_end ?? '',
  };
}

// When plan_included_credits is absent (Legacy plans), all credits are additional.
// Do NOT fall back to total_limit — that makes additionalTotal = 0.
export function deriveCreditsAmounts(summary: {
  plan_included_credits?: number | null;
  total_used?: number | null;
  total_limit?: number | null;
}): {
  planIncluded: number;
  planUsed: number;
  additionalTotal: number;
  additionalUsed: number;
} {
  const planIncluded = summary.plan_included_credits ?? 0;
  const totalUsed = summary.total_used ?? 0;
  return {
    planIncluded,
    planUsed: Math.min(totalUsed, planIncluded),
    additionalTotal: (summary.total_limit ?? 0) - planIncluded,
    additionalUsed: Math.max(totalUsed - planIncluded, 0),
  };
}

/**
 * Custom hook to manage WhatsApp API templates, creation, editing, and UI state.
 * Provides logic for searching, sorting, formik integration, and template data injection.
 * @returns {tWhatsappApiHook} Hook API for WhatsApp API management
 */
export default function useWhatsappApi(): tWhatsappApiHook {
  const [templates, setTemplates] = useState<tTemplateTable[]>([]);
  // Whether getAndParseTemplates has resolved at least once for this workspace —
  // consumers gate "no templates" UI on this to avoid flashing that state
  // before the first fetch (or the not-connected early return) settles.
  const [templatesHydrated, setTemplatesHydrated] = useState(false);
  // INT-1544 — whether the latest getAndParseTemplates call failed. On failure
  // the list keeps the previous answer, so a consumer that needs the status
  // Meta just sent must not read it.
  const [templatesFetchFailed, setTemplatesFetchFailed] = useState(false);
  const [templateCounter, setTemplateCounter] = useState<tSentTemplateCounter>();
  const [undeliveredMessage] = useState<tUndeliveredMessage>();
  const { t: tWhatsapp } = useTranslation('whatsappApi/whatsappApi');
  const [filteredTemplates, setFilteredTemplates] = useState<tTemplateTable[]>(
    [],
  );
  // RET-2027: the credits group (`credits/*`, one-time purchase) is Owner or
  // Admin in leadbilling. This provider is mounted app-wide and mounts before
  // WorkspaceProvider answers with the role, which reads SELLER until then.
  // AGI-320: `roleResolved` is true only once this workspace's own user list
  // gave the caller's role.
  const { currentWorkspaceId, isRoleResolved: roleResolved } =
    useWorkspaceContext();
  const { canPurchaseCredits } = useBillingPermissions();
  const { whatsappBusinessIntegration } = useIntegrationsContext();
  const [whatsappApiWindow, setWhatsappApiWindow] =
    useState<eWhatsappApiWindow>(eWhatsappApiWindow.GENERAL);
  const [loading, setLoading] = useState<boolean>(false);
  // INT-1544 — set by a template status notification, read once by TemplatesTab.
  const [templateLink, setTemplateLink] =
    useState<tWhatsappApiHook['templateLink']>(null);
  const openTemplateLink = useCallback((templateName: string) => {
    setTemplateLink({ templateName });
    setWhatsappApiWindow(eWhatsappApiWindow.VIEW_TEMPLATES);
  }, []);
  const clearTemplateLink = useCallback(() => setTemplateLink(null), []);
  const [mediaPreview, setMediaPreview] = useState<string | null>(null);
  const [mediaType, setMediaType] = useState<eMediaType | null>(null);
  const [showInput, setShowInput] = useState<eSelectedInput | null>(null);
  const [step, setStep] = useState<number>(1);
  const { addAlert } = useToastContext();
  const { handleError } = useErrorHandler();
  const [editMode, setEditMode] = useState<boolean>(false);
  const [templateToEdit, setTemplateToEdit] = useState<tTemplateTable | null>(null);
  // INT-1494 — template drafts, shared by the table, the wizard and the notice.
  const templateDraftsState = useTemplateDraftsState();
  // INT-1300 — set when the user leaves the Flows list through "Usar en una
  // plantilla"; read by CreateUpdateTemplate to show the context banner and
  // the "Volver a Flows" link. Lives here (not in FlowsSection) because the
  // window switch FLOWS -> CREATE_TEMPLATE unmounts both views.
  const [pendingFlowForTemplate, setPendingFlowForTemplate] =
    useState<tPendingFlowForTemplate | null>(null);
  // INT-1500 U2 — the category a template created from the chat starts on:
  // a 131049 notice with no approved follow-up template offers "Crear
  // plantilla de seguimiento" and lands on CREATE_TEMPLATE with UTILITY
  // chosen. Read and released by useCreateUpdateTemplate.
  const [pendingTemplateCategory, setPendingTemplateCategory] =
    useState<eTemplateCategory | null>(null);
  const [sortProperty, setSortProperty] = useState<keyof tTemplateTable | null>(
    null,
  );
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const { t: tErrors } = useTranslation('errors/errors');
  const wapiState = whatsappBusinessIntegration.state;
  const isConnected = wapiState === eIntegrationStatus.CONNECTED;
  const { parseComponentIntoJson } = useTemplatesTable();
  const [usageRecord, setUsageRecord] = useState<tUsageRecord | null>(null);
  const [statisticsRecord, setStatisticsRecord] =
    useState<tGetStatisticsResponse | null>(null);
  const [selectedPeriod, setSelectedPeriod] = useState('last_7_days');
  const [accountData, setAccountData] = useState<tGetAccountResponse | null>(
    null,
  );

  const [accountHealthStatus, setAccountHealthStatus] =
    useState<tAccountHealthStatus>('OK');
  // INT-1101: Meta policy-violation code (SPAM, SCAM, TOBACCO, ...) driving
  // the specific banner AccountHealthBanner renders instead of the generic
  // count-based HIGH_SPAMMING copy. Null when the current health status is
  // not sourced from a meta_violation event.
  const [violationType, setViolationType] = useState<string | null>(null);

  // Legacy slot kept on the context surface until callers migrate to the new
  // `creditPackages` BFF-driven slice. The setter is unused now — the preset
  // list comes from Stripe via `PurchaseCreditsModal`, not from a hardcoded
  // tier table — so we drop it from the destructuring.
  const [oneTimeCreditProducts] = useState<tOneTimeCreditProduct[]>([]);
  const [oneTimeCreditPaymentMethods, setOneTimeCreditPaymentMethods] =
    useState<tPaymentMethodOption[]>([]);
  const [fetchingOneTimeCreditData, setFetchingOneTimeCreditData] =
    useState(false);

  // General Tab - redesign state
  const [accountInfo, setAccountInfo] = useState<tAccountInfo | null>(null);
  const [creditsInfo, setCreditsInfo] = useState<tCreditsInfo | null>(null);
  const [messageStatistics, setMessageStatistics] =
    useState<tMessageStatistics | null>(null);
  const [notifications, setNotifications] = useState<tWapiNotification[]>([]);
  const [notificationsLoading, setNotificationsLoading] = useState(false);
  const [notificationsCursor, setNotificationsCursor] = useState<string | null>(
    null,
  );
  const [notificationsHasMore, setNotificationsHasMore] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [panelOpenedAt, setPanelOpenedAt] = useState<string>('');
  const [notificationFilter, setNotificationFilter] = useState<
    'pending' | 'all'
  >('pending');
  const [notificationCategory, setNotificationCategory] =
    useState<tNotificationCategory | null>(null);
  const defaultStatsDates = useMemo(
    () => ({
      start: dayjs().subtract(30, 'day').format('YYYY-MM-DD'),
      end: dayjs().format('YYYY-MM-DD'),
    }),
    [],
  );
  const [statisticsDateRange, setStatisticsDateRange] =
    useState<tStatisticsDateRange>(defaultStatsDates);
  const [showInvalidDateModal, setShowInvalidDateModal] = useState(false);
  const [invalidDateReason, setInvalidDateReason] = useState<
    'future' | 'range_exceeded' | 'start_after_end'
  >('future');
  const [showQualityModal, setShowQualityModal] = useState(false);

  // GRO-811: One-Time Credits — Figma alignment (additive only; wired in Phase 2+)
  const [creditPackages, setCreditPackages] = useState<tCreditPackage[] | null>(
    null,
  );
  // Per-credit unit cost (cents) for the workspace currency. Populated alongside
  // `creditPackages` from the Stripe-driven `/credits/packages` BFF response.
  const [creditUnitPriceCents, setCreditUnitPriceCents] = useState<
    number | null
  >(null);
  // Authoritative workspace currency for the credits flow. Populated alongside
  // `creditUnitPriceCents` from the `/credits/packages` BFF response so the
  // custom-amount path can label the total in the real workspace currency
  // instead of a hardcoded `'MXN'` fallback (GRO-811).
  const [creditCurrency, setCreditCurrency] = useState<string | null>(null);
  const [paymentMethods, setPaymentMethods] = useState<
    tPaymentMethod[] | null
  >(null);
  const [creditsBalance, setCreditsBalance] = useState<tCreditsBalance | null>(
    null,
  );
  const [purchaseHistory, setPurchaseHistory] = useState<
    tPaginated<tPurchaseHistoryItem> | null
  >(null);
  const [purchaseDraft, setPurchaseDraft] = useState<tPurchaseDraft>({});
  const [currentStep, setCurrentStep] = useState<tPurchaseStep>('idle');

  /**
   * GRO-811 auto-return — when the user comes back from Settings →
   * Subscription after adding a payment method (via SubscriptionManager's
   * redirect on save), re-open the purchase modal at the select step IF
   * there's a draft to resume. The draft survives the round-trip because
   * it lives in this provider (mounted at the app root). The query param
   * is cleared so a hard refresh doesn't re-trigger the reopen.
   */
  const [searchParams, setSearchParams] = useSearchParams();
  const { isWalletEnabled, isLoadingWalletFlag } = useWalletFlag();
  useEffect(() => {
    if (searchParams.get('returnFrom') !== 'credits_purchase') return;
    // RET-2027: until WorkspaceProvider answers, the role reads SELLER and
    // `canPurchaseCredits` is false. Consuming the param now would drop an
    // Owner's or Admin's reopen for good, so keep it until the role resolves;
    // WorkspaceProvider sets the role and `roleResolved` in the same render.
    if (!roleResolved) return;
    const hasDraft = Object.keys(purchaseDraft).length > 0;
    // RET-2027: the funnel reopens only for a role leadbilling lets buy.
    if (hasDraft && canPurchaseCredits) setCurrentStep('select');
    const next = new URLSearchParams(searchParams);
    next.delete('returnFrom');
    setSearchParams(next, { replace: true });
    // Intentionally exclude `purchaseDraft` and `canPurchaseCredits` from the
    // deps: this fires on the navigation in, and once more when the role
    // resolves, not on every draft mutation or role read.
  }, [searchParams, roleResolved]);

  /**
   * AGI-141 — deep-link into the Flows window from outside this page (the
   * Lead Agent config's empty-state "Crear Flow" CTA navigates here with
   * `?window=flows`). Mirrors the `returnFrom` pattern above: consume once,
   * then strip the param so a refresh doesn't re-trigger the jump.
   */
  useEffect(() => {
    if (searchParams.get('window')?.toLowerCase() !== 'flows') return;
    setWhatsappApiWindow(eWhatsappApiWindow.FLOWS);
    const next = new URLSearchParams(searchParams);
    next.delete('window');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  /**
   * RET-1689 — same deep-link, now for the Saldo window: the balance card in
   * Configuración de cuenta -> Suscripción has no movements table of its own,
   * so its "ver la actividad" link navigates here with `?window=balance`.
   *
   * Gated on the flag because the Saldo tab does not exist without it —
   * selecting it there would land the user on a page with nothing rendered.
   *
   * While the flags are still in flight the map holds DEFAULT_FLAGS (this
   * flag false), so consuming the param then would strip it and drop the jump
   * on the floor. Wait for the answer; the param survives a render or two.
   */
  useEffect(() => {
    if (searchParams.get('window')?.toLowerCase() !== 'balance') return;
    if (isLoadingWalletFlag) return;
    if (isWalletEnabled) setWhatsappApiWindow(eWhatsappApiWindow.BALANCE);
    const next = new URLSearchParams(searchParams);
    next.delete('window');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, isWalletEnabled, isLoadingWalletFlag]);

  /**
   * AGI-216 — same deep-link pattern as Flows above (the Home "Novedades"
   * card's `campaign-pacing` item navigates here with its "Ir a Campañas"
   * CTA via `?window=campaigns`). No flag gate: unlike Flows/Saldo, the
   * CAMPAIGNS tab always exists, so there's no "land on nothing rendered"
   * risk to guard against.
   */
  useEffect(() => {
    if (searchParams.get('window')?.toLowerCase() !== 'campaigns') return;
    setWhatsappApiWindow(eWhatsappApiWindow.CAMPAIGNS);
    const next = new URLSearchParams(searchParams);
    next.delete('window');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  /**
   * INT-1653 — same deep-link pattern, for the "Crear plantilla" button of the
   * disconnection notice ("otra cuenta": the templates did not move to the new
   * account) via `?window=create_template`. Like Campaigns, the window always
   * exists, so there is no flag to wait for.
   */
  useEffect(() => {
    if (searchParams.get('window')?.toLowerCase() !== 'create_template') return;
    setWhatsappApiWindow(eWhatsappApiWindow.CREATE_TEMPLATE);
    const next = new URLSearchParams(searchParams);
    next.delete('window');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const notificationFilterRef = useRef(notificationFilter);
  notificationFilterRef.current = notificationFilter;
  const notificationCategoryRef = useRef(notificationCategory);
  notificationCategoryRef.current = notificationCategory;
  const notificationsCursorRef = useRef(notificationsCursor);
  notificationsCursorRef.current = notificationsCursor;
  const notificationsLoadingRef = useRef(notificationsLoading);
  notificationsLoadingRef.current = notificationsLoading;
  // INT-1516 — the record-gated effects below refetch on every flip between
  // two record states, so a call made before the flip can still be in flight
  // when the one made after it settles, and nothing orders their answers.
  // Monotonic tokens, as in IntegrationsProvider: only the newest call of each
  // read writes, so the failed record's "reconnect" notification cannot land
  // back over the list the reconnect refetched.
  const notificationsFetchSeqRef = useRef(0);
  const unreadCountFetchSeqRef = useRef(0);

  const fetchNotifications = useCallback(
    async (cursor?: string) => {
      if (!currentWorkspaceId) return;
      const seq = ++notificationsFetchSeqRef.current;
      setNotificationsLoading(true);
      try {
        const response = await WhatsappApiService.getNotifications(
          currentWorkspaceId,
          {
            limit: 20,
            cursor: cursor ?? undefined,
            category: notificationCategoryRef.current ?? undefined,
            is_read:
              notificationFilterRef.current === 'pending' ? false : undefined,
          },
        );
        if (seq !== notificationsFetchSeqRef.current) return;
        if (response?.data) {
          const incoming = response.data.notifications;
          setNotifications((prev) =>
            cursor ? [...prev, ...incoming] : incoming,
          );
          setNotificationsCursor(response.data.next_cursor || null);
          setNotificationsHasMore(response.data.has_more);
        }
      } catch (error) {
        console.error('Error fetching notifications:', error);
      } finally {
        // Guarded like the writes: a superseded call clearing the flag would
        // let "load more" page off the list the newest call is about to replace.
        if (seq === notificationsFetchSeqRef.current) {
          setNotificationsLoading(false);
        }
      }
    },
    [currentWorkspaceId],
  );

  const loadMoreNotifications = useCallback(async () => {
    if (
      notificationsCursorRef.current &&
      notificationsHasMore &&
      !notificationsLoadingRef.current
    ) {
      await fetchNotifications(notificationsCursorRef.current);
    }
  }, [notificationsHasMore, fetchNotifications]);

  const fetchUnreadCount = useCallback(async () => {
    if (!currentWorkspaceId) return;
    const seq = ++unreadCountFetchSeqRef.current;
    try {
      const response =
        await WhatsappApiService.getUnreadNotificationCount(currentWorkspaceId);
      if (seq !== unreadCountFetchSeqRef.current) return;
      if (response?.data) {
        setUnreadCount(response.data.unread_count);
      }
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  }, [currentWorkspaceId]);

  const markNotificationRead = useCallback(
    async (id: number) => {
      if (!currentWorkspaceId) return;
      const target = notifications.find((n) => n.id === id);
      if (!target || target.is_read) return;

      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, is_read: true } : n)),
      );
      setUnreadCount((prev) => Math.max(prev - 1, 0));
      try {
        await WhatsappApiService.markNotificationAsRead(
          currentWorkspaceId,
          id,
        );
      } catch (error) {
        console.error('Error marking notification as read:', error);
        setNotifications((prev) =>
          prev.map((n) => (n.id === id ? { ...n, is_read: false } : n)),
        );
        setUnreadCount((prev) => prev + 1);
      }
    },
    [currentWorkspaceId, notifications],
  );

  const markAllNotificationsRead = useCallback(async () => {
    if (!currentWorkspaceId) return;
    const before = panelOpenedAt || new Date().toISOString();
    const previousNotifications = [...notifications];
    const previousUnread = unreadCount;
    setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
    setUnreadCount(0);
    try {
      await WhatsappApiService.markAllNotificationsAsRead(
        currentWorkspaceId,
        before,
      );
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      setNotifications(previousNotifications);
      setUnreadCount(previousUnread);
    }
  }, [currentWorkspaceId, notifications, unreadCount, panelOpenedAt]);

  const handleStatisticsDateChange = useCallback(
    (field: 'start' | 'end', value: string) => {
      const now = dayjs();
      const selected = dayjs(value);
      if (selected.isAfter(now)) {
        setInvalidDateReason('future');
        setShowInvalidDateModal(true);
        return;
      }
      setStatisticsDateRange((prev) => {
        const updated = { ...prev, [field]: value };
        // Validate start <= end
        if (updated.start && updated.end && dayjs(updated.start).isAfter(dayjs(updated.end))) {
          setInvalidDateReason('start_after_end');
          setShowInvalidDateModal(true);
          return prev;
        }
        return updated;
      });
    },
    [],
  );

  useEffect(() => {
    if (accountData) {
      const raw = accountData.quality_rating ?? 'UNKNOWN';
      const rating = QUALITY_RATING_MAP[raw] ?? eQualityRating.UNKNOWN;
      const labelKey = QUALITY_LABEL_KEYS[raw] ?? QUALITY_LABEL_KEYS.UNKNOWN;
      const messagingLimit = resolveDailyMessageLimit(accountData);

      const verificationStatus = resolveBusinessVerificationStatus(
        accountData.business_verification_status,
      );
      const businessVerification = verificationStatus
        ? {
            rawStatus: verificationStatus,
            bucket:
              BUSINESS_VERIFICATION_BUCKET_MAP[verificationStatus] ??
              eBusinessVerificationBucket.UNKNOWN,
            label: tWhatsapp(
              BUSINESS_VERIFICATION_LABEL_KEYS[verificationStatus] ??
                BUSINESS_VERIFICATION_LABEL_KEYS[
                  eBusinessVerificationStatus.UNKNOWN
                ],
            ),
          }
        : null;

      const canSendMessage = resolveCanSendMessage(
        accountData.can_send_message,
      );

      setAccountInfo({
        name: accountData.name,
        phone: accountData.phone_number || accountData.phone_number_id,
        qualityRating: {
          rating,
          label: tWhatsapp(labelKey),
          description: '',
        },
        status:
          accountData.phone_number_status === 'CONNECTED'
            ? tWhatsapp('page.general.account_info_card.status_connected')
            : tWhatsapp('page.general.account_info_card.status_disconnected'),
        rawStatus: accountData.phone_number_status || 'UNKNOWN',
        dailyMessageLimit: messagingLimit,
        dailyMessagesSent: accountData.daily_messages_sent ?? 0,
        businessVerification,
        canSendMessage,
        // Go serializes an absent string as "" — coalesce with || not ??.
        sendRestrictionReason: accountData.send_restriction_reason || '',
        // INT-951: `is_coex` is a real boolean (not a string the Go backend
        // empty-serializes), so `??` is correct here — `false` is a valid
        // value we must not coerce, and `undefined` (absent field) safely
        // degrades to today's non-Coex behavior.
        isCoex: accountData.is_coex ?? false,
      });
    }
  }, [accountData, tWhatsapp]);

  // creditsInfo is updated directly inside getCredits()
  // when the full response with usage_summary is received

  const [statisticsLoading, setStatisticsLoading] = useState(false);

  const getDetailedStatistics = useCallback(
    async (startDate?: string, endDate?: string) => {
      if (!currentWorkspaceId) return;
      setStatisticsLoading(true);
      try {
        const response =
          await WhatsappApiService.getDetailedMessagesStatistics(
            currentWorkspaceId,
            startDate,
            endDate,
          );
        if (response?.data) {
          const d = response.data;
          setMessageStatistics({
            allMessages: {
              sent: d.all_messages.sent,
              delivered: d.all_messages.delivered,
            },
            byCategory: {
              marketing: d.delivered_by_category.marketing,
              utility: d.delivered_by_category.utility,
              authentication: d.delivered_by_category.authentication,
            },
            freeMessages: {
              total: d.free_messages.total,
              window24h: d.free_messages.window_24h,
              window72h: d.free_messages.window_72h,
            },
            paidMessages: {
              total: d.paid_delivered.total,
              marketing: d.paid_delivered.by_category.marketing,
              utility: d.paid_delivered.by_category.utility,
              authentication: d.paid_delivered.by_category.authentication,
            },
          });
        }
      } catch (error) {
        console.error('Failed to fetch detailed statistics:', error);
      } finally {
        setStatisticsLoading(false);
      }
    },
    [currentWorkspaceId],
  );

  const detailedStatsIntervalRef = useRef<ReturnType<typeof setInterval>>();

  // Fetch detailed statistics when date range changes + 60s auto-refresh
  // Only polls when General tab is active and browser tab is visible
  useEffect(() => {
    const cleanup = () => {
      if (detailedStatsIntervalRef.current) {
        clearInterval(detailedStatsIntervalRef.current);
        detailedStatsIntervalRef.current = undefined;
      }
    };

    // INT-1492 — no account, no statistics to poll (every call was a 404).
    if (whatsappApiWindow !== eWhatsappApiWindow.GENERAL || !isConnected) {
      return cleanup;
    }

    const { start, end } = statisticsDateRange;
    if (start && end) {
      const startDj = dayjs(start);
      const endDj = dayjs(end);
      if (endDj.diff(startDj, 'day') > 90) {
        setInvalidDateReason('range_exceeded');
        setShowInvalidDateModal(true);
        return cleanup;
      }
      getDetailedStatistics(start, end);

      detailedStatsIntervalRef.current = setInterval(() => {
        if (document.visibilityState === 'visible') {
          getDetailedStatistics(start, end);
        }
      }, 60_000);
    }

    return cleanup;
  }, [statisticsDateRange, getDetailedStatistics, whatsappApiWindow, isConnected]);

  const { trackWapiEvent } = useWapiAnalytics();
  const {
    createNewWapiTemplateEvent,
    updateWapiTemplateEvent,
    accountHealthUpdatedEvent,
    wapiNotificationCreatedEvent,
  } = useSocketsContext();
  const [templateStatus, setTemplateStatus] = useState<
    eTemplatesTable | undefined
  >(undefined);

  /**
   * Subscribes to socket events for new template creation and updates.
   * When a new template is created, it parses the components and updates the templates state.
   * Validates that the template doesn't already exist to prevent duplicates.
   * @param {tCreateNewWapiTemplateEvent} data - The event data containing template creation information
   * @returns {void}
   */
  createNewWapiTemplateEvent.useSubscription(
    (data: tCreateNewWapiTemplateEvent) => {
      if (data && data.template_id) {
        const existingTemplate = templates.find(
          (template) => template.id === data.template_id,
        );
        if (existingTemplate) {
          return;
        }

        const componentJson = parseComponentIntoJson(data.components);
        const componentText =
          (componentJson && componentJson[1] && componentJson[1].text) ||
          (componentJson && componentJson[0] && componentJson[0].text) ||
          '';

        const parsedTemplate: tTemplateTable = {
          id: data.template_id,
          template_type: data.template_type,
          title: data.name,
          text: componentText,
          language: data.lang,
          components: componentJson,
          status: data.status as eTemplatesTable,
          lastModified: new Date().toISOString(),
          impactedLeads: data.impacted_leads,
          readingIndex: data.reading_index,
          rate: data.rate ?? '',
          reason: data?.rejected_reason,
        };

        setTemplates((prevTemplates) => [parsedTemplate, ...prevTemplates]);
        setFilteredTemplates((prevFiltered) => [
          parsedTemplate,
          ...prevFiltered,
        ]);
      }
    },
  );

  const VALID_HEALTH_STATUSES: Set<string> = new Set(['OK', 'LOW_SPAMMING', 'HIGH_SPAMMING', 'RESTRICTED']);

  accountHealthUpdatedEvent.useSubscription(
    (data: tAccountHealthUpdatedEvent) => {
      if (
        data?.health_status &&
        data.workspace_id === currentWorkspaceId &&
        VALID_HEALTH_STATUSES.has(data.health_status)
      ) {
        setAccountHealthStatus(data.health_status as tAccountHealthStatus);
        setViolationType(data.violation_type ?? null);
      }
    },
  );

  wapiNotificationCreatedEvent.useSubscription(
    (data: tWapiNotificationCreatedEvent) => {
      if (data?.workspace_id === currentWorkspaceId) {
        const notification: tWapiNotification = {
          id: data.id,
          workspace_id: data.workspace_id,
          notification_type: data.notification_type,
          title: data.title,
          description: data.description,
          severity: data.severity,
          is_read: false,
          action_url: data.action_url ?? null,
          category: data.category,
          created_at: data.created_at,
        };
        setNotifications((prev) => {
          if (prev.some((n) => n.id === data.id)) return prev;
          setUnreadCount((c) => c + 1);
          return [notification, ...prev];
        });
      }
    },
  );

  /**
   * Subscribes to socket events for template updates.
   * When a template is updated, it finds the template by ID and updates its status and
   * reason in both the templates and filteredTemplates state.
   * @param {tUpdateWapiTemplateEvent} data - The event data containing template update information
   * @returns {void}
   */
  updateWapiTemplateEvent.useSubscription((data: tUpdateWapiTemplateEvent) => {
    if (data && data.template_id) {
      let templateTitle: string | undefined;

      setTemplates((prevTemplates) => {
        const template = prevTemplates.find((t) => t.id === data.template_id);

        if (!template) {
          return prevTemplates;
        }

        templateTitle = template.title;

        return prevTemplates.map((t) =>
          t.id === data.template_id ? applyTemplateStatusUpdate(t, data) : t,
        );
      });

      setFilteredTemplates((prevFiltered) => {
        const template = prevFiltered.find((t) => t.id === data.template_id);

        if (!template) {
          return prevFiltered;
        }

        return prevFiltered.map((t) =>
          t.id === data.template_id ? applyTemplateStatusUpdate(t, data) : t,
        );
      });

      if (data.status === 'APPROVED') {
        trackWapiEvent(WAPI_EVENTS.TEMPLATE_STATUS_APPROVED, {
          templateId: data.template_id,
          templateName: templateTitle ?? '',
          receivedAt: new Date().toISOString(),
        });
      } else if (data.status === 'REJECTED') {
        trackWapiEvent(WAPI_EVENTS.TEMPLATE_STATUS_REJECTED, {
          templateId: data.template_id,
          templateName: templateTitle ?? '',
          rejectionReason: data.reason ?? '',
        });
      }

      if (templateTitle) {
        addAlert({
          type: eAlertType.SUCCESS,
          message: `La plantilla ${templateTitle} ha sido actualizada correctamente.`,
        });
      }
    }
  });

  /**
   * Filters the templates list based on a search query.
   * @param {string} query - The search string to filter templates
   */
  const searchTemplates = async (query: string) => {
    setLoading(true);
    try {
      if (!query.trim()) {
        setFilteredTemplates(templates);
        return;
      }
      const lowerCaseQuery = query.toLowerCase();
      const filtered = templates.filter((template) =>
        template.title.toLowerCase().includes(lowerCaseQuery),
      );
      setFilteredTemplates(filtered);
    } catch {
      // search errors are non-fatal
    } finally {
      setLoading(false);
    }
  }

  /**
   * Handles the form submission for creating or editing a WhatsApp template.
   * Builds the payload and calls the appropriate API service.
   * Shows success or error alerts.
   */
  const handleSubmit = async () => {
    setLoading(true);
    try {
      if (!currentWorkspaceId) {
        throw new Error('No workspace ID found');
      }

      const components = buildTemplateComponents();
      const payload = createTemplatePayload(components);
      await saveTemplate(payload);

      const eventName = editMode
        ? WAPI_EVENTS.TEMPLATE_EDIT
        : WAPI_EVENTS.TEMPLATE_CREATE;
      trackWapiEvent(eventName, {
        status: 'success',
        templateName: createTemplateFormik.values.name,
        templateCategory: createTemplateFormik.values.templateType,
        templateLanguage: payload.language || 'es_MX',
        creationSource: 'dashboard',
      });

      showSuccessAlert();
      resetFormAndState();
    } catch (error) {
      handleSubmissionError(error);
      setStep(2);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Creates all template components based on formik values
   * @returns {tComponent[]} Array of template components
   */
  const buildTemplateComponents = (): tComponent[] => {
    const components: tComponent[] = [];

    // Add header component if needed
    const headerComponent = buildHeaderComponent();
    if (headerComponent) {
      components.push(headerComponent);
    }

    // Add body component (always required)
    components.push(buildBodyComponent());

    // Add footer component if it exists
    const footerComponent = buildFooterComponent();
    if (footerComponent) {
      components.push(footerComponent);
    }

    // Add buttons component if any buttons exist
    const buttonsComponent = buildButtonsComponent();
    if (buttonsComponent) {
      components.push(buttonsComponent);
    }

    return components;
  };

  /**
   * Builds the header component based on form values
   * @returns {tComponent | null} Header component or null if not needed
   */
  const buildHeaderComponent = (): tComponent | null => {
    if (
      createTemplateFormik.values.header === '' &&
      !createTemplateFormik.values.media
    ) {
      return null;
    }
    if (createTemplateFormik.values.media) {
      return {
        type: eComponentType.HEADER,
        format: eFormatType.MEDIA,
        example: {
          header_handle: [`${createTemplateFormik.values.media}`],
        },
      };
    }

    const headerComponent: tComponent = {
      type: eComponentType.HEADER,
      format: eFormatType.TEXT,
      text: createTemplateFormik.values.header,
    };

    const headerVariable = createTemplateFormik.values.headerVariable?.trim();
    if (headerVariable) {
      headerComponent.example = {
        header_text_named_params: [
          {
            param_name: headerVariable,
            example: createTemplateFormik.values.headerExample,
          },
        ],
      };
    }

    return headerComponent;
  };

  /**
   * Builds the body component with any variables
   * @returns {tComponent} Body component
   */
  const buildBodyComponent = (): tComponent => {
    const bodyComponent: tComponent = {
      type: eComponentType.BODY,
      text: createTemplateFormik.values.text,
    };

    const bodyParams = createTemplateFormik.values.variables.filter(
      (param) => param.name && param.name.trim() !== '',
    );

    if (bodyParams.length > 0) {
      bodyComponent.example = {
        body_text_named_params: bodyParams.map((param) => ({
          param_name: param.name,
          example: param.example,
        })),
      };
    }

    return bodyComponent;
  };

  /**
   * Builds the footer component if it exists
   * @returns {tComponent | null} Footer component or null
   */
  const buildFooterComponent = (): tComponent | null => {
    const footer = createTemplateFormik.values.footer?.trim();
    if (!footer) {
      return null;
    }

    return {
      type: eComponentType.FOOTER,
      text: footer,
    };
  };

  /**
   * Builds the buttons component if any buttons exist
   * @returns {tComponent | null} Buttons component or null
   */
  const buildButtonsComponent = (): tComponent | null => {
    if (!createTemplateFormik.values.buttons?.length) {
      return null;
    }

    const filteredButtons: tButton[] = createTemplateFormik.values.buttons
      .filter((button) => button.text && button.text.trim() !== '')
      .map((button) => {
        const buttonObj: tButton = {
          index: button.index,
          type: button.type,
          text: button.text,
        };

        if (button.url) {
          buttonObj.url = button.url;
        }

        if (button.example) {
          buttonObj.example = button.example;
        }

        if (button.phone_number && button.phone_number.trim() !== '') {
          buttonObj.phone_number = button.phone_number.replace(/\s+/g, '');
        }

        return buttonObj;
      });

    if (!filteredButtons.length) {
      return null;
    }

    return {
      type: eComponentType.BUTTONS,
      buttons: filteredButtons,
    };
  };

  /**
   * Creates the template payload with all components
   * @param {tComponent[]} components - The template components
   * @returns {tCreateTemplateRequest} The complete template payload
   */
  const createTemplatePayload = (
    components: tComponent[],
  ): tCreateTemplateRequest => {
    return {
      name: createTemplateFormik.values.name,
      language: 'es_MX',
      category: createTemplateFormik.values.templateType,
      parameter_format: eParameterFormat.NAMED,
      components,
    };
  };

  /**
   * Saves the template using the appropriate API service
   * @param {tCreateTemplateRequest} payload - The template payload
   */
  const saveTemplate = async (
    payload: tCreateTemplateRequest,
  ): Promise<void> => {
    if (editMode && currentWorkspaceId) {
      await WhatsappApiService.updateTemplate(
        currentWorkspaceId,
        createTemplateFormik.values.name,
        payload,
      );
    } else {
      if (!currentWorkspaceId) {
        throw new Error('No workspace ID found');
      }
      await WhatsappApiService.createNewTemplate(currentWorkspaceId, payload);
    }
  };

  /**
   * Shows a success alert message
   */
  const showSuccessAlert = (): void => {
    addAlert({
      type: eAlertType.SUCCESS,
      message: `Tu plantilla se ha ${editMode ? 'editado' : 'creado'} correctamente.`,
    });
  };

  /**
   * Resets form and component state
   */
  const resetFormAndState = (): void => {
    createTemplateFormik.resetForm();
    setMediaPreview(null);
    setMediaType(null);
    setStep(1);
    setWhatsappApiWindow(eWhatsappApiWindow.VIEW_TEMPLATES);
  };

  /**
   * Handles any errors during submission
   * @param {unknown} error - The error that occurred
   */
  const handleSubmissionError = (error: unknown): void => {
    const entry = handleError(
      error,
      editMode ? tErrors('wapi.editTemplateError') : tErrors('wapi.createTemplateError'),
    );

    const errorMessage =
      (entry?.messageKey ? tErrors(entry.messageKey) : undefined) ||
      (error instanceof Error
        ? error.message
        : `Error al ${editMode ? 'editar' : 'crear'} tu plantilla. Por favor, inténtalo de nuevo.`);

    const eventName = editMode
      ? WAPI_EVENTS.TEMPLATE_EDIT
      : WAPI_EVENTS.TEMPLATE_CREATE;
    trackWapiEvent(eventName, {
      status: 'error',
      errorMessage,
    });
  };

  const createTemplateFormik = useFormik({
    initialValues: initialCreateTemplateFormikValues,
    validationSchema: WhatsappApiSchema,
    onSubmit: handleSubmit,
  });

  /**
   * Detects media type from URL or file extension
   * @param {string} url - The media URL to analyze
   * @returns {eMediaType} The detected media type
   */
  const detectMediaTypeFromUrl = (url: string): eMediaType => {
    const lowerUrl = url.toLowerCase();

    if (lowerUrl.includes('.pdf') || lowerUrl.includes('application/pdf')) {
      return eMediaType.PDF;
    }

    if (lowerUrl.match(/\.(mp4|mov|avi|wmv|flv|webm)($|\?)/)) {
      return eMediaType.VIDEO;
    }

    // Default to image for other files or unknown types
    return eMediaType.IMAGE;
  }

  /**
   * Extracts variables from a template component (header or body).
   * @param {tTemplateTable} data - The template data
   * @param {eComponentType} type - The component type (HEADER or BODY)
   * @returns {tVariable[]} Array of variables with name and example
   */
  const getVariablesFromData = (data: tTemplateTable, type: eComponentType) => {
    const component = data.components?.find((c) => c.type === type);
    if (!component || !component.example) {
      return [];
    }
    const exampleParams =
      type === eComponentType.BODY
        ? component.example.body_text_named_params
        : component.example.header_text_named_params;
    if (!exampleParams) {
      return [];
    }

    return exampleParams.map((param) => ({
      name: param.param_name,
      example: param.example,
    })) as tVariable[];
  }

  /**
   * Extracts button data from a template.
   * @param {tTemplateTable} data - The template data
   * @returns {tButton[]} Array of button objects
   */
  const getButtonsFromData = (data: tTemplateTable) => {
    const component = data.components?.find(
      (c) => c.type === eComponentType.BUTTONS,
    );
    if (!component || !component.buttons) {
      return [];
    }
    return component.buttons.map((button, index) => ({
      index: index,
      type: button.type,
      text: button.text,
      url: button.url || '',
      phoneNumber: button.phone_number || '',
    })) as tButton[];
  }

  /**
   * Injects template data into the formik form for editing.
   * Sets edit mode and prepares the UI for editing.
   * @param {tTemplateTable} data - The template data to inject
   */
  const injectDataToFormik = (data: tTemplateTable) => {
    setEditMode(true);
    setWhatsappApiWindow(eWhatsappApiWindow.CREATE_TEMPLATE);
    setStep(1);
    if (data?.components?.find((c) => c.type === eComponentType.HEADER)?.text) {
      setShowInput(eSelectedInput.TEXT);
    }

    if (
      data?.components?.find((c) => c.type === eComponentType.HEADER)
        ?.format === eFormatType.MEDIA
    ) {
      setShowInput(eSelectedInput.MEDIA);
      const mediaUrl =
        data?.components?.find((c) => c.type === eComponentType.HEADER)?.example
          ?.header_handle?.[0] || null;

      if (mediaUrl) {
        setMediaPreview(mediaUrl);
        setMediaType(detectMediaTypeFromUrl(mediaUrl));
      }
    } else {
      setShowInput(null);
      setMediaPreview(null);
      setMediaType(null);
    }

    setTemplateToEdit(data);

    createTemplateFormik.setValues({
      name: data.title,
      templateType:
        data.template_type === 'FOLLOWING' || data.template_type === 'MARKETING'
          ? eTemplateCategory.MARKETING
          : eTemplateCategory.UTILITY,
      header:
        data?.components?.find((c) => c.type === eComponentType.HEADER)?.text ||
        '',
      text:
        data?.components?.find((c) => c.type === eComponentType.BODY)?.text ||
        '',
      footer:
        data?.components?.find((c) => c.type === eComponentType.FOOTER)?.text ||
        '',
      headerVariable:
        data?.components?.find((c) => c.type === eComponentType.HEADER)?.example
          ?.header_text_named_params?.[0]?.param_name || '',
      headerExample:
        data?.components?.find((c) => c.type === eComponentType.HEADER)?.example
          ?.header_text_named_params?.[0]?.example || '',
      variables: getVariablesFromData(data, eComponentType.BODY),
      buttons: getButtonsFromData(data),
    });
  }

  /**
   * Sorts the filtered templates by a given property and toggles sort order.
   * @param {keyof tTemplateTable} property - The property to sort by
   */
  const sortBy = (property: keyof tTemplateTable) => {
    let order: 'asc' | 'desc' = 'asc';
    if (sortProperty === property) {
      order = sortOrder === 'asc' ? 'desc' : 'asc';
    }
    setSortProperty(property);
    setSortOrder(order);

    const sortedTemplates = [...filteredTemplates].sort((a, b) => {
      const aValue = a?.[property];
      const bValue = b?.[property];
      if (aValue == null || bValue == null) return 0;
      if (aValue < bValue) return order === 'asc' ? -1 : 1;
      if (aValue > bValue) return order === 'asc' ? 1 : -1;
      return 0;
    });
    setFilteredTemplates(sortedTemplates);
  }

  const syncTemplates = async () => {
    const syncStartTime = Date.now();
    trackWapiEvent(WAPI_EVENTS.TEMPLATE_SYNC_START, {
      syncTrigger: 'manual',
    });
    try {
      setLoading(true);
      await WhatsappApiService.fetchTemplates(currentWorkspaceId ?? '');
      trackWapiEvent(WAPI_EVENTS.TEMPLATE_SYNC_COMPLETE, {
        syncDuration: Date.now() - syncStartTime,
      });
    } catch (err) {
      const entry = handleError(err, tErrors('wapi.syncTemplates'));

      const errorMessage =
        (entry?.messageKey ? tErrors(entry.messageKey) : undefined) ||
        (err instanceof Error
          ? err.message
          : tErrors('wapi.syncTemplates'));

      trackWapiEvent(WAPI_EVENTS.TEMPLATE_SYNC_ERROR, {
        errorType: entry?.code ?? 'unknown',
        errorMessage,
      });
    }
    setLoading(false);
  }

  const getAndParseTemplates = async () => {
    if (!isConnected) {
      setTemplatesHydrated(true);
      return;
    }
    try {
      setLoading(true);
      setTemplatesFetchFailed(false);
      const response = await WhatsappApiService.getTemplates(
        currentWorkspaceId ?? '',
      );
      if (response ) {
        if(response.templates) {
        const parsedTemplates: tTemplateTable[] = response.templates.map(
          (template) => {
            const componentJson = parseComponentIntoJson(template.components);
            const componentText =
              (componentJson && componentJson[1] && componentJson[1].text) ||
              (componentJson && componentJson[0] && componentJson[0].text) ||
              '';

            return {
              id: template.template_id,
              template_type: template.template_type,
              title: template.name,
              text: componentText,
              language: template.lang,
              components: componentJson,
              status: template.status as eTemplatesTable,
              lastModified: new Date(template.updated_at).toISOString(),
              impactedLeads: template.impacted_leads,
              readingIndex: template.reading_index,
              rate: template.rate ?? '',
              reason: template?.rejected_reason,
              rejectionInfo: toRejectionInfo(template.rejection_info),
              // INT-1122: propagate the flow-attachment fields the backend
              // resolves. Without these, templateNeedsFlowBinding always
              // sees flow_uuid as undefined and the "Vincular Flow" icon
              // stays visible on a row that has just been bound — permitting
              // the operator to rebind and silently overwrite the persisted
              // attachment.
              flow_uuid: template.flow_uuid ?? undefined,
              flow_name: template.flow_name ?? undefined,
              flow_action: template.flow_action ?? undefined,
              flow_cta_text: template.flow_cta_text ?? undefined,
              flow_first_screen: template.flow_first_screen ?? undefined,
              // INT-1554: drives the category-change icon of the templates table.
              previous_category: template.previous_category ?? null,
              category_changed_at: template.category_changed_at ?? null,
              category_change_acknowledged_at: template.category_change_acknowledged_at ?? null,
              category_review_deadline: template.category_review_deadline ?? null,
            };
          },
        );
        setTemplates(parsedTemplates);
        setFilteredTemplates(parsedTemplates);
      }
    }
    } catch (error) {
      setTemplatesFetchFailed(true);
      handleError(error, tErrors('wapi.getTemplates'));
    }
    setLoading(false);
    setTemplatesHydrated(true);
  };

  /**
   * Fetches WhatsApp template credits usage for the current workspace.
   * Requires a valid workspace ID.
   * @returns {Promise<void>} Promise that resolves when the operation is complete
   */
  const getCredits = async () => {
    if (!currentWorkspaceId) {
      return;
    }
    // Hydrate the BFF balance (`/credits/balance`) ahead of the legacy
    // fetch. leadbilling computes the plan/additional split from each
    // feature_usages row's own used_count (plan = product_type NULL row,
    // additional = ONE_TIME rows), whereas the legacy `with-usage` summary
    // below can't express it — deriveCreditsAmounts attributes usage
    // plan-first, which inverts the pools whenever consumption came from a
    // one-time top-up. CreditsTab/CreditsCard prefer `creditsBalance` and
    // only fall back to the derived `creditsInfo` when this fetch fails.
    //
    // RET-2027: `credits/balance` admits only the Owner and Admins, so a
    // seller never asks; they read the legacy derivation, which is what the
    // refused request used to leave them with anyway.
    if (canPurchaseCredits) {
      try {
        setCreditsBalance(
          await CreditsService.getCreditsBalance(currentWorkspaceId),
        );
      } catch {
        // Non-blocking — consumers fall back to the legacy derivation.
      }
    }
    try {
      const response =
        await WhatsappApiService.getTemplateCreditsUsage(currentWorkspaceId);
      if (response?.data?.usage_summary && response?.data?.usage_records) {
        const activeRecord = response.data.usage_records.find(
          (record) => record.is_active,
        );
        const mostRecentRecord = activeRecord || response.data.usage_records[0];

        const consolidatedRecord: tUsageRecord = {
          id: mostRecentRecord?.id || 0,
          period_start: mostRecentRecord?.period_start || '',
          period_end: mostRecentRecord?.period_end || '',
          used_count: response.data.usage_summary.total_used ?? 0,
          limit: response.data.usage_summary.total_limit ?? 0,
          is_active: mostRecentRecord?.is_active || false,
        };
        setUsageRecord(consolidatedRecord);

        const summary = response.data.usage_summary;
        const { planIncluded, planUsed, additionalTotal, additionalUsed } =
          deriveCreditsAmounts(summary);
        const { planPeriodEnd, addonExpirationDate } = deriveCreditsDateInfo(
          response.data.usage_records,
          planIncluded,
        );

        setCreditsInfo({
          plan: {
            used: planUsed,
            total: planIncluded,
            periodEnd: planPeriodEnd,
          },
          additional: {
            used: additionalUsed,
            total: additionalTotal,
            expirationDate: addonExpirationDate,
          },
        });
      }
    } catch {
      // errors are handled by callers
    }
  }

  /**
   * Fetches sent messages statistics for the current workspace within an optional date range.
   * @param startDate
   * @param endDate
   * @returns {Promise<void>} Promise that resolves when the operation is complete
   */
  const getStatistics = async (
    startDate?: string,
    endDate?: string,
  ): Promise<void> => {
    if (!currentWorkspaceId) {
      return;
    }
    try {
      const response = await WhatsappApiService.getSentMessagesStatistics(
        currentWorkspaceId,
        startDate,
        endDate,
      );
      if (response?.data) {
        setStatisticsRecord(response);
      }
    } catch {
      // errors are handled by callers
    }
  }

  const getPeriodDates = (period: string) => {
    const today = dayjs();
    let startDate = '';
    let endDate = '';
    switch (period) {
      case 'last_7_days':
        startDate = today
          .subtract(6, 'day')
          .startOf('day')
          .format('YYYY-MM-DD 00:00:00');
        endDate = today.endOf('day').format('YYYY-MM-DD 23:59:59');
        break;
      case 'last_30_days':
        startDate = today
          .subtract(29, 'day')
          .startOf('day')
          .format('YYYY-MM-DD 00:00:00');
        endDate = today.endOf('day').format('YYYY-MM-DD 23:59:59');
        break;
      case 'last_90_days':
        startDate = today
          .subtract(89, 'day')
          .startOf('day')
          .format('YYYY-MM-DD 00:00:00');
        endDate = today.endOf('day').format('YYYY-MM-DD 23:59:59');
        break;
      case 'last_180_days':
        startDate = today
          .subtract(179, 'day')
          .startOf('day')
          .format('YYYY-MM-DD 00:00:00');
        endDate = today.endOf('day').format('YYYY-MM-DD 23:59:59');
        break;
      case 'last_365_days':
        startDate = today
          .subtract(364, 'day')
          .startOf('day')
          .format('YYYY-MM-DD 00:00:00');
        endDate = today.endOf('day').format('YYYY-MM-DD 23:59:59');
        break;
      case 'this_month':
        startDate = today.startOf('month').format('YYYY-MM-DD 00:00:00');
        endDate = today.endOf('day').format('YYYY-MM-DD 23:59:59');
        break;
      case 'last_month':
        startDate = today
          .subtract(1, 'month')
          .startOf('month')
          .format('YYYY-MM-DD 00:00:00');
        endDate = today
          .subtract(1, 'month')
          .endOf('month')
          .format('YYYY-MM-DD 23:59:59');
        break;
      case 'this_year':
        startDate = today.startOf('year').format('YYYY-MM-DD 00:00:00');
        endDate = today.endOf('day').format('YYYY-MM-DD 23:59:59');
        break;
      case 'last_year':
        startDate = today
          .subtract(1, 'year')
          .startOf('year')
          .format('YYYY-MM-DD 00:00:00');
        endDate = today
          .subtract(1, 'year')
          .endOf('year')
          .format('YYYY-MM-DD 23:59:59');
        break;
      default:
        startDate = today
          .subtract(6, 'day')
          .startOf('day')
          .format('YYYY-MM-DD 00:00:00');
        endDate = today.endOf('day').format('YYYY-MM-DD 23:59:59');
    }
    return { startDate, endDate };
  }

  const handlePeriodChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setSelectedPeriod(value);
    const { startDate, endDate } = getPeriodDates(value);
    getStatistics(startDate, endDate);
  }

  // Same token as the notification reads (INT-1516): this read refetches on
  // every flip too, and the older answer describes the record as it was.
  const accountInfoFetchSeqRef = useRef(0);
  const getAccountInfo = async () => {
    if (!currentWorkspaceId) {
      return;
    }
    const seq = ++accountInfoFetchSeqRef.current;
    try {
      const response =
        await WhatsappApiService.getAccountData(currentWorkspaceId);
      if (seq !== accountInfoFetchSeqRef.current) return;
      if (response) {
        setAccountData(response);
        if (response.account_health_status) {
          setAccountHealthStatus(response.account_health_status);
          setViolationType(response.violation_type ?? null);
        }
      }
    } catch {
      // errors are handled by callers
    }
  }

  const fetchOneTimeCreditData = useCallback(async (): Promise<void> => {
    if (!currentWorkspaceId) return;
    setFetchingOneTimeCreditData(true);
    // Post Stripe-only refactor (GRO-811): the legacy hardcoded preset list
    // is gone — `creditPackages` in the context is hydrated from the BFF by
    // `PurchaseCreditsModal` via `CreditsService.getCreditPackages`. We keep
    // this fetcher around because it also pulls the payment-methods list,
    // which the legacy widget still consumes via `oneTimeCreditPaymentMethods`.
    try {
      const { paymentMethods, defaultPaymentMethodId } =
        await OneTimeCreditsService.getPaymentMethods(currentWorkspaceId);
      // Default-first so the modal's "first card" auto-select lands on the
      // customer's default instead of an arbitrary card.
      setOneTimeCreditPaymentMethods(
        sortPaymentMethodsByDefaultFirst(paymentMethods, defaultPaymentMethodId),
      );
    } finally {
      setFetchingOneTimeCreditData(false);
    }
  }, [currentWorkspaceId]);

  const purchaseOneTimeCredits = async (
    priceId: string,
    paymentMethodId: string,
  ): Promise<void> => {
    if (!currentWorkspaceId) {
      throw new Error('No workspace ID found');
    }

    try {
      const response = await OneTimeCreditsService.purchaseCredits(
        currentWorkspaceId,
        { price_id: priceId, payment_method_id: paymentMethodId },
      );

      if ('requires_action' in response && response.requires_action) {
        // Reuse the module-scope Stripe handle from src/constants/stripe.constants
        // so 3DS challenges share the same loader as the rest of the app.
        // Calling `loadStripe(...)` inline here would create a second Stripe.js
        // instance and contradict the singleton invariant the constants file
        // documents.
        const stripe = await stripePromise;
        if (!stripe) {
          throw new Error('Stripe failed to load');
        }
        const { error } = await stripe.confirmCardPayment(
          response.client_secret,
        );
        if (error) {
          throw new Error(error.message ?? 'Payment authentication failed');
        }
      } else if ((response as Record<string, unknown>).success !== true) {
        throw Object.assign(new Error('internal_error'), {
          type: 'internal' as const,
        });
      }

      await getCredits();
    } catch (rawError: unknown) {
      // Defer to the centralised classifier in Credits.service so this
      // legacy hook handler shares the same error taxonomy as
      // `OneTimeCredits.service.purchaseCredits` — see `mapCreditsServiceError`
      // for the status-code table. `mapCreditsServiceError` always
      // throws — the bare `throw rawError` afterwards exists only so TS
      // can prove the function never falls through.
      mapCreditsServiceError(rawError);
      throw rawError;
    }
  };

  // RET-2027: waits for the role so the balance above is asked once, by the
  // roles that may read it. Keyed on the permission so a role that changes
  // under the same workspace re-reads with the right answer.
  useEffect(() => {
    if (!currentWorkspaceId || !roleResolved) return;
    getCredits();
  }, [currentWorkspaceId, roleResolved, canPurchaseCredits]);

  // INT-1492 — everything below reads the workspace's WhatsApp account, and
  // this provider is mounted app-wide: without an account each of these
  // answered 404 on every page load, and the account card waited on a
  // skeleton forever.
  //
  // INT-1516 KTD9 — the gate is "an account record exists", not "connected":
  // a held or failed record answers GET /account, and its card and its
  // notifications (the ACTIVATION_FAILED one included) are what tell the
  // customer what happened to the number. The effects are keyed on the state
  // VALUE, not on a boolean: held and connected both open the gate, so a
  // boolean would not change on the flip and the card would keep the held
  // record's data. The refetch is also what drops a resolved ACTIVATION_FAILED
  // notification from the panel, since websocket does not relay
  // `notification.resolved` yet.
  useEffect(() => {
    if (!currentWorkspaceId || !hasWapiAccountRecord(wapiState)) return;
    getAccountInfo();
    fetchUnreadCount();
    setPanelOpenedAt(new Date().toISOString());
  }, [currentWorkspaceId, wapiState]);

  // Statistics describe traffic, and a record Meta has not activated has none:
  // they stay behind CONNECTED, and fire the moment the account connects.
  useEffect(() => {
    if (!currentWorkspaceId || !isConnected) return;
    const { startDate, endDate } = getPeriodDates(selectedPeriod);
    getStatistics(startDate, endDate);
  }, [currentWorkspaceId, isConnected]);

  // Re-fetch when filter or category changes, and on every state change (above).
  useEffect(() => {
    if (!hasWapiAccountRecord(wapiState)) return;
    fetchNotifications();
  }, [notificationFilter, notificationCategory, fetchNotifications, wapiState]);

  /**
   * Edit Eligibility Validation
   * Checks if a template can be edited based on its status
   * @param {eTemplatesTable} status - The template status
   * @returns {boolean} true if it can be edited, false otherwise
   */
  const canEditTemplateByStatus = (status: eTemplatesTable): boolean => {
    const editableStatuses = [
      eTemplatesTable.APPROVED,
      eTemplatesTable.REJECTED,
      eTemplatesTable.PAUSED,
    ];
    return editableStatuses.includes(status);
  };

  /**
   * 24-hour Cooldown Validation
   * Checks if 24 hours have passed since the last modification for approved templates
   * @param {tTemplateTable} template - The template to check
   * @returns {boolean} true if it can be edited (cooldown met), false otherwise
   */
  const canEditTemplateByCooldown = (template: tTemplateTable): boolean => {
    if (template.status !== eTemplatesTable.APPROVED) {
      return true;
    }

    const lastModified = dayjs(template.lastModified);
    const now = dayjs();
    const hoursSinceLastEdit = now.diff(lastModified, 'hour');

    return hoursSinceLastEdit >= 24;
  };

  /**
   * Full edit eligibility validation
   * Combines all business rule validations
   * @param {tTemplateTable} template - The template to validate
   * @returns {{canEdit: boolean, reason?: string}} Validation result
   */
  const validateTemplateEditEligibility = (
    template: tTemplateTable,
  ): {
    canEdit: boolean;
    reason?: string;
  } => {
    if (!canEditTemplateByStatus(template.status)) {
      return {
        canEdit: false,
        reason: tWhatsapp('page.errors.edit-error', {
          status: template.status,
        }),
      };
    }

    if (!canEditTemplateByCooldown(template)) {
      const lastModified = dayjs(template.lastModified);
      const nextEditTime = lastModified.add(24, 'hour');
      const hoursRemaining = nextEditTime.diff(dayjs(), 'hour');

      return {
        canEdit: false,
        reason: tWhatsapp('page.errors.wait-error', {
          remaining: hoursRemaining + 1,
        }),
      };
    }

    return { canEdit: true };
  };

  return {
    templates,
    setTemplates,
    templatesHydrated,
    templatesFetchFailed,
    templateLink,
    openTemplateLink,
    clearTemplateLink,
    filteredTemplates,
    setFilteredTemplates,
    loading,
    setLoading,
    searchTemplates,
    whatsappApiWindow,
    setWhatsappApiWindow,
    createTemplateFormik,
    mediaPreview,
    setMediaPreview,
    mediaType,
    setMediaType,
    showInput,
    setShowInput,
    step,
    setStep,
    editMode,
    setEditMode,
    templateToEdit,
    setTemplateToEdit,
    ...templateDraftsState,
    pendingFlowForTemplate,
    setPendingFlowForTemplate,
    pendingTemplateCategory,
    setPendingTemplateCategory,
    injectDataToFormik,
    sortBy,
    syncTemplates,
    getAndParseTemplates,
    usageRecord,
    setUsageRecord,
    statisticsRecord,
    setStatisticsRecord,
    getStatistics,
    selectedPeriod,
    setSelectedPeriod,
    handlePeriodChange,
    accountData,
    setAccountData,
    validateTemplateEditEligibility,
    setTemplateStatus,
    templateStatus,
    templateCounter,
    undeliveredMessage,
    setTemplateCounter,
    accountHealthStatus,
    violationType,
    purchaseOneTimeCredits,
    refreshCredits: getCredits,
    fetchOneTimeCreditData,
    oneTimeCreditProducts,
    oneTimeCreditPaymentMethods,
    fetchingOneTimeCreditData,
    // General Tab - redesign
    accountInfo,
    creditsInfo,
    messageStatistics,
    notifications,
    notificationsLoading,
    notificationsHasMore,
    unreadCount,
    loadMoreNotifications,
    markNotificationRead,
    markAllNotificationsRead,
    notificationFilter,
    setNotificationFilter,
    notificationCategory,
    setNotificationCategory,
    statisticsLoading,
    statisticsDateRange,
    handleStatisticsDateChange,
    showInvalidDateModal,
    setShowInvalidDateModal,
    invalidDateReason,
    showQualityModal,
    setShowQualityModal,
    // GRO-811: One-Time Credits — Figma alignment
    creditPackages,
    setCreditPackages,
    creditUnitPriceCents,
    setCreditUnitPriceCents,
    creditCurrency,
    setCreditCurrency,
    paymentMethods,
    setPaymentMethods,
    creditsBalance,
    setCreditsBalance,
    purchaseHistory,
    setPurchaseHistory,
    purchaseDraft,
    setPurchaseDraft,
    currentStep,
    setCurrentStep,
  };
}
